/**
 * PostWizard — the universal + POST modal for SocialPulse Local.
 *
 * Flow (§4 of the master build prompt):
 *   1. STEP_INTENT: user picks intent (or arrives with one preselected)
 *   2. STEP_DESCRIBE: user types a sentence; AI intake fills the draft
 *   3. STEP_REVIEW: user reviews / edits generated fields + location + price
 *   4. STEP_DONE: link to the new listing
 *
 * Small on purpose — keeps to one file so it can be dropped into any
 * page. All complex parsing / heuristics live on the backend
 * (`/api/local/intake/parse`).
 */
import { useEffect, useMemo, useState } from "react";
import { X, Sparkles, ArrowRight, Loader2, MapPin, CheckCircle2, Plus } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { localApi, LISTING_TYPES, typeByKey, COMPENSATION_LABEL } from "@/lib/localApi";
import { toast } from "sonner";
import TranslatableInput from "@/components/TranslatableInput";

const STEP = { INTENT: 1, DESCRIBE: 2, REVIEW: 3, DONE: 4 };


export default function PostWizard({ open, onClose, preselectType, countryCode = "US" }) {
  const nav = useNavigate();
  const [step, setStep] = useState(STEP.INTENT);
  const [type, setType] = useState(preselectType || null);
  const [text, setText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiUnavailable, setAiUnavailable] = useState(false);
  const [draft, setDraft] = useState(null);       // parsed by AI
  const [country, setCountry] = useState(countryCode);
  const [countries, setCountries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [createdId, setCreatedId] = useState(null);
  const [city, setCity] = useState("");
  const [priceMinor, setPriceMinor] = useState("");
  const [compensation, setCompensation] = useState("fixed_price");

  useEffect(() => {
    if (!open) return;
    setStep(preselectType ? STEP.DESCRIBE : STEP.INTENT);
    setType(preselectType || null);
    setText(""); setDraft(null); setCreatedId(null);
    setAiUnavailable(false);
    setCity(""); setPriceMinor("");
    localApi.get("/countries?active_only=true").then((r) => setCountries(r.data.items || []));
    localApi.get("/categories").then((r) => setCategories(r.data.items || []));
  }, [open, preselectType]);

  const currency = useMemo(
    () => countries.find((c) => c.code === country)?.currency || "USD",
    [countries, country],
  );

  if (!open) return null;

  // ── AI Intake ────────────────────────────────────────────────
  async function runAI() {
    setAiBusy(true);
    setAiUnavailable(false);
    try {
      const { data } = await localApi.post("/intake/parse", {
        text,
        country_code: country,
      });
      setDraft(data);
      if (data.listing_type) setType(data.listing_type);
      if (data.suggested_price_minor) setPriceMinor(String(data.suggested_price_minor));
      if (data.compensation_hint) setCompensation(data.compensation_hint);
      if (data.ai_unavailable) setAiUnavailable(true);
      setStep(STEP.REVIEW);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        toast.error("Please sign in first — SocialPulse account required to post.");
      } else {
        toast.error("Couldn't reach the AI helper. You can still fill in the form manually.");
        setDraft({
          listing_type: type || "sell",
          title: text.slice(0, 80),
          description: text,
          category_slug: null,
          tags: [], safety_warnings: [], missing_fields: [],
          confidence: 0, ai_unavailable: true,
        });
        setAiUnavailable(true);
        setStep(STEP.REVIEW);
      }
    } finally {
      setAiBusy(false);
    }
  }

  // ── Create listing ───────────────────────────────────────────
  async function submit() {
    if (!draft) return;
    setSaving(true);
    try {
      // Description may be a plain string or a {__base, en, ml, ...} dict.
      // Backend currently accepts a string; use the base translation.
      const desc = draft.description;
      const descStr = typeof desc === "string" ? desc :
                      desc?.[desc?.__base || "en"] || desc?.en || "";
      const pricing = [];
      const parsed = priceMinor ? parseInt(priceMinor, 10) : null;
      const t = typeByKey(type || draft.listing_type);
      if (t.needsPrice && parsed) {
        pricing.push({ model: compensation, amount_minor: parsed, currency });
      } else if (draft.compensation_hint === "free" || type === "free" || type?.startsWith("food_free")) {
        pricing.push({ model: "free" });
      } else if (compensation && !parsed) {
        pricing.push({ model: compensation });
      }
      const body = {
        listing_type: type || draft.listing_type,
        title: draft.title,
        description: descStr,
        categories: draft.category_slug ? [draft.category_slug] : [],
        location: {
          country_code: country,
          city: city || null,
        },
        pricing,
        media: [],
        tags: draft.tags || [],
        attributes: typeof desc === "object" ? { descriptions_i18n: desc } : {},
      };
      const { data } = await localApi.post("/listings", body);
      setCreatedId(data.id);
      setStep(STEP.DONE);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Something went wrong.";
      toast.error(String(detail));
    } finally {
      setSaving(false);
    }
  }

  // ── UI ────────────────────────────────────────────────────────
  return (
    <div
      data-testid="post-wizard"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-[720px] rounded-lg bg-white shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          data-testid="wizard-close"
          className="absolute top-4 right-4 opacity-60 hover:opacity-100"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Step body */}
        <div className="p-8">
          {step === STEP.INTENT && (
            <div data-testid="wizard-step-intent">
              <div className="text-[11px] font-mono uppercase tracking-[0.2em] opacity-60 mb-2">
                Step 1 of 3
              </div>
              <h2 className="font-display text-2xl md:text-3xl mb-2">
                What would you like to do?
              </h2>
              <p className="text-sm opacity-70 mb-6">
                Pick the one that fits best — you can change it in the next step.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {LISTING_TYPES.map((t) => (
                  <button
                    key={t.key}
                    data-testid={`wizard-intent-${t.key}`}
                    className="rounded-md border px-3 py-3 text-left text-sm hover:-translate-y-0.5 transition"
                    style={{ borderColor: "var(--wofy-border)" }}
                    onClick={() => { setType(t.key); setStep(STEP.DESCRIBE); }}
                  >
                    <div className="font-medium">{t.label}</div>
                    <div className="text-[11px] opacity-60 uppercase tracking-wider mt-0.5">
                      {t.intent}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === STEP.DESCRIBE && (
            <div data-testid="wizard-step-describe">
              <div className="text-[11px] font-mono uppercase tracking-[0.2em] opacity-60 mb-2">
                Step 2 of 3 · {typeByKey(type).label}
              </div>
              <h2 className="font-display text-2xl md:text-3xl mb-2">
                Describe it in one sentence
              </h2>
              <p className="text-sm opacity-70 mb-6">
                Our AI helper will draft the title, category and price
                — you'll review before posting.
              </p>
              <textarea
                data-testid="wizard-describe-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={4}
                placeholder="e.g. Solid wood dining table with six chairs, good condition, must go by weekend"
                className="w-full rounded-md border px-4 py-3 text-[15px] outline-none focus:border-emerald-500"
                style={{ borderColor: "var(--wofy-border)" }}
              />
              <div className="mt-4 flex items-center justify-between">
                <button
                  className="text-sm opacity-60 hover:opacity-100"
                  onClick={() => setStep(STEP.INTENT)}
                >
                  ← Change intent
                </button>
                <button
                  data-testid="wizard-ai-run"
                  disabled={text.trim().length < 6 || aiBusy}
                  onClick={runAI}
                  className="wofy-btn-primary inline-flex items-center gap-2 px-5 py-2.5 disabled:opacity-40"
                >
                  {aiBusy ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Thinking…</>
                  ) : (
                    <><Sparkles className="w-4 h-4" /> Draft with AI</>
                  )}
                </button>
              </div>
            </div>
          )}

          {step === STEP.REVIEW && draft && (
            <div data-testid="wizard-step-review">
              <div className="text-[11px] font-mono uppercase tracking-[0.2em] opacity-60 mb-2">
                Step 3 of 3 · Review
              </div>
              <h2 className="font-display text-2xl md:text-3xl mb-2">
                Looking good?
              </h2>
              {aiUnavailable && (
                <div className="mb-4 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[13px]">
                  AI helper is unavailable — please fill this in manually.
                </div>
              )}

              <label className="block mt-4">
                <span className="text-[12px] uppercase tracking-wider opacity-70">Title</span>
                <input
                  data-testid="wizard-review-title"
                  value={draft.title || ""}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  className="mt-1 w-full rounded-md border px-3 py-2 text-[15px] outline-none"
                  style={{ borderColor: "var(--wofy-border)" }}
                />
              </label>

              <label className="block mt-4">
                <span className="text-[12px] uppercase tracking-wider opacity-70">Description</span>
                <TranslatableInput
                  as="textarea"
                  rows={3}
                  value={draft.description || ""}
                  onChange={(v) => setDraft({ ...draft, description: v })}
                  testid="wizard-review-description"
                  inputCls="mt-1 w-full rounded-md border px-3 py-2 text-[14px] outline-none"
                />
              </label>

              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[12px] uppercase tracking-wider opacity-70">Type</span>
                  <select
                    data-testid="wizard-review-type"
                    value={type || draft.listing_type}
                    onChange={(e) => setType(e.target.value)}
                    className="mt-1 w-full rounded-md border px-3 py-2 text-[14px]"
                    style={{ borderColor: "var(--wofy-border)" }}
                  >
                    {LISTING_TYPES.map((t) => (
                      <option key={t.key} value={t.key}>{t.label}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-[12px] uppercase tracking-wider opacity-70">Category</span>
                  <select
                    data-testid="wizard-review-category"
                    value={draft.category_slug || ""}
                    onChange={(e) => setDraft({ ...draft, category_slug: e.target.value })}
                    className="mt-1 w-full rounded-md border px-3 py-2 text-[14px]"
                    style={{ borderColor: "var(--wofy-border)" }}
                  >
                    <option value="">(none)</option>
                    {categories.map((c) => (
                      <option key={c.slug} value={c.slug}>
                        {c.parent_slug ? `└ ${c.name}` : `${c.icon || ""} ${c.name}`}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="mt-4 grid sm:grid-cols-3 gap-3">
                <label className="block">
                  <span className="text-[12px] uppercase tracking-wider opacity-70">Country</span>
                  <select
                    data-testid="wizard-review-country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="mt-1 w-full rounded-md border px-3 py-2 text-[14px]"
                    style={{ borderColor: "var(--wofy-border)" }}
                  >
                    {countries.map((c) => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-[12px] uppercase tracking-wider opacity-70">City / area</span>
                  <input
                    data-testid="wizard-review-city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="e.g. Portland"
                    className="mt-1 w-full rounded-md border px-3 py-2 text-[14px]"
                    style={{ borderColor: "var(--wofy-border)" }}
                  />
                </label>
                <label className="block">
                  <span className="text-[12px] uppercase tracking-wider opacity-70">
                    Price ({currency})
                  </span>
                  <input
                    data-testid="wizard-review-price"
                    value={priceMinor ? (priceMinor / 100).toFixed(2) : ""}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value || "0");
                      setPriceMinor(isNaN(v) ? "" : String(Math.round(v * 100)));
                    }}
                    placeholder="0.00"
                    className="mt-1 w-full rounded-md border px-3 py-2 text-[14px]"
                    style={{ borderColor: "var(--wofy-border)" }}
                  />
                </label>
              </div>

              <div className="mt-4">
                <span className="text-[12px] uppercase tracking-wider opacity-70">
                  Compensation model
                </span>
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(COMPENSATION_LABEL).map(([k, v]) => (
                    <button
                      key={k}
                      data-testid={`wizard-comp-${k}`}
                      onClick={() => setCompensation(k)}
                      className="rounded-full px-3 py-1 text-[12px] border transition"
                      style={{
                        borderColor: compensation === k ? "#0E7C55" : "var(--wofy-border)",
                        background: compensation === k ? "rgba(14,124,85,0.08)" : "white",
                        color: compensation === k ? "#0E7C55" : "var(--wofy-text)",
                      }}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>

              {draft.safety_warnings?.length > 0 && (
                <div className="mt-4 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-[13px]">
                  <b className="mr-1">Heads-up:</b> {draft.safety_warnings.join(" · ")}
                </div>
              )}

              <div className="mt-8 flex items-center justify-between">
                <button
                  className="text-sm opacity-60 hover:opacity-100"
                  onClick={() => setStep(STEP.DESCRIBE)}
                >
                  ← Edit sentence
                </button>
                <button
                  data-testid="wizard-submit"
                  disabled={saving || !draft.title}
                  onClick={submit}
                  className="wofy-btn-primary inline-flex items-center gap-2 px-5 py-2.5 disabled:opacity-40"
                >
                  {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Posting…</> :
                            <><Plus className="w-4 h-4" /> Post listing</>}
                </button>
              </div>
            </div>
          )}

          {step === STEP.DONE && createdId && (
            <div data-testid="wizard-step-done" className="text-center py-6">
              <CheckCircle2 className="w-14 h-14 mx-auto mb-4" style={{ color: "#0E7C55" }} />
              <h2 className="font-display text-2xl md:text-3xl mb-2">Live!</h2>
              <p className="opacity-70 mb-6">
                Your listing is now visible to your local community.
              </p>
              <div className="flex items-center justify-center gap-3">
                <Link
                  to={`/local/listings/${createdId}`}
                  data-testid="wizard-view-listing"
                  className="wofy-btn-primary inline-flex items-center gap-2"
                  onClick={onClose}
                >
                  View listing <ArrowRight className="w-4 h-4" />
                </Link>
                <button
                  onClick={() => {
                    setStep(STEP.INTENT); setType(null); setText(""); setDraft(null); setCreatedId(null);
                  }}
                  className="wofy-btn-ghost inline-flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" /> Post another
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
